import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, CheckIcon, StarIcon } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
const ease = [0.22, 1, 0.36, 1] as const;
export function Hero() {
  const {
    t
  } = useLanguage();
  return <section id="top" className="relative overflow-hidden bg-white pt-[72px]" data-id="element-175" data-mp="huo1e2ebseqg">
      <div className="grid-lines pointer-events-none absolute inset-0 opacity-70" aria-hidden="true" data-id="element-176" data-mp="hsl1mqndf78j" />
      <div className="pointer-events-none absolute -left-40 -top-40 h-[520px] w-[520px] rounded-full bg-brand/[0.07] blur-3xl" aria-hidden="true" data-id="element-177" data-mp="ms7ozswwy3v2" />

      <div className="relative mx-auto max-w-shell px-5 pb-16 pt-14 sm:px-8 sm:pb-24 sm:pt-20 lg:pt-24" data-id="element-178" data-mp="vndq4el8nxr9">
        <div className="grid items-center gap-14 lg:grid-cols-[1.05fr_1fr] lg:gap-16" data-id="element-179" data-mp="10k43uv9kmh9i">
          <div data-id="element-180" data-mp="15ll36mcepvnz">
            <motion.div initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            ease
          }} className="inline-flex items-center gap-2.5 rounded-full border border-ink/10 bg-white/70 px-4 py-2 shadow-soft backdrop-blur" data-id="element-181" data-mp="15ltj6n3tth3e">
              <span className="flex items-center gap-0.5" aria-hidden="true" data-id="element-182" data-mp="3vur7iqjezex5">
                {Array.from({
                length: 5
              }).map((_, i) => <StarIcon key={i} className="h-3.5 w-3.5 fill-brand text-brand" data-id="element-183" data-mp="3rv0ba7b4c6l8" data-mpi={i} />)}
              </span>
              <span className="text-[13px] font-medium text-ink/75" data-id="element-184" data-mp="sditkhrt4z7p">{t.hero.badge}</span>
            </motion.div>

            <motion.h1 initial={{
            opacity: 0,
            y: 22
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.7,
            delay: 0.08,
            ease
          }} className="headline mt-7 text-[40px] font-bold text-ink sm:text-[56px] lg:text-[64px]" data-id="element-185" data-mp="nhah5c4zvk48">
              {t.hero.headline} <span className="text-brand" data-id="element-186" data-mp="16d0lh60egsla">{t.hero.headlineAccent}</span>
            </motion.h1>

            <motion.p initial={{
            opacity: 0,
            y: 22
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.7,
            delay: 0.16,
            ease
          }} className="mt-7 max-w-[560px] text-[17px] leading-relaxed text-ink/65 sm:text-[19px]" data-id="element-187" data-mp="30mypuhruz3g0">
              {t.hero.subtitle}
            </motion.p>

            <motion.div initial={{
            opacity: 0,
            y: 22
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.7,
            delay: 0.24,
            ease
          }} className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center" data-id="element-188" data-mp="2hv67riebv2n2">
              <a href="#consultation" className="group inline-flex items-center justify-center gap-2 rounded-2xl bg-brand px-7 py-4 text-[15px] font-semibold text-white shadow-glow transition-all duration-300 hover:-translate-y-0.5 hover:bg-brand-600" data-id="element-189" data-mp="2cx4gia6qzx0i">
                {t.hero.primaryCta}
                <ArrowRightIcon className="h-[18px] w-[18px] transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" data-id="element-190" data-mp="1vo24i5g2utjc" />
              </a>
              <a href="#services" className="inline-flex items-center justify-center rounded-2xl border border-ink/12 bg-white px-7 py-4 text-[15px] font-semibold text-ink shadow-soft transition-all duration-300 hover:-translate-y-0.5 hover:border-ink/25" data-id="element-191" data-mp="25ctu46xo11r3">
                {t.hero.secondaryCta}
              </a>
            </motion.div>
          </div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.97,
          y: 24
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} transition={{
          duration: 0.9,
          delay: 0.2,
          ease
        }} className="relative" data-id="element-192" data-mp="2abdgu9k32hki">
            <div className="overflow-hidden rounded-4xl border border-ink/[0.06] shadow-lift" data-id="element-193" data-mp="tygbbghrrchx">
              <img src="https://cdn.magicpatterns.com/patterns/generated-images/192a17d1-11c4-4692-acc3-78efcc5fe028.jpg" alt={t.hero.imageAlt} className="h-[340px] w-full object-cover sm:h-[460px] lg:h-[520px]" data-id="element-194" data-mp="ozvjo5gl2153" />
            </div>
            <motion.div initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.7,
            delay: 0.55,
            ease
          }} className="absolute -bottom-6 left-4 right-4 rounded-3xl border border-white/50 bg-white/70 p-5 shadow-lift backdrop-blur-xl sm:left-8 sm:right-auto sm:w-[300px]" data-id="element-195" data-mp="vgx15f9dzh17">
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-brand" data-id="element-196" data-mp="hnfjk009gedt">
                {t.hero.statLabel}
              </p>
              <p className="mt-2 text-[28px] font-bold leading-none text-ink" data-id="element-197" data-mp="mexkio2aaeys">{t.hero.statValue}</p>
              <p className="mt-2 text-[13px] leading-snug text-ink/60" data-id="element-198" data-mp="3l3cuwca5rsdq">{t.hero.statBody}</p>
            </motion.div>
          </motion.div>
        </div>

        <motion.ul initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.7,
        delay: 0.65,
        ease
      }} className="mt-24 flex flex-wrap items-center justify-center gap-x-3 gap-y-3 rounded-3xl border border-ink/[0.07] bg-mist/80 p-4 backdrop-blur sm:mt-28 sm:gap-x-6" data-id="element-199" data-mp="2784g0ecczfzt">
          {t.hero.trustIndicators.map(item => <li key={item} className="flex items-center gap-2 px-2" data-id="element-200" data-mp="1o90b9hruo8dy">
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-brand/10" data-id="element-201" data-mp="1obd10h3unn1k">
                <CheckIcon className="h-3 w-3 text-brand" aria-hidden="true" data-id="element-202" data-mp="1jgfkyq85kdf6" />
              </span>
              <span className="text-[13px] font-medium text-ink/75 sm:text-[14px]" data-id="element-203" data-mp="1feucri6kcnbb">{item}</span>
            </li>)}
        </motion.ul>
      </div>
    </section>;
}